import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import toast from "react-hot-toast";

import GenericTable from "@/components/ui/GenericTable";

import {
    formatDate,
    getSpecialOffers,
    updateSpecialOffer,
} from "@/services/offers";

import { useScreenPermission } from "@/hooks/useScreenPermission";
import Pagination from "../common/Pagination";
import SpecialOfferModal from "./AddSpecialOffers";

const ITEMS_PER_PAGE = 20;

export default function SpecialOfferList() {
    const [loading, setLoading] = useState(false);
    const [offers, setOffers] = useState<any[]>([]);
    const [editingOffer, setEditingOffer] = useState<any | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const { canCreate, canUpdate } =
        useScreenPermission("special offer");

    const fetchOffers = async (
        page: number,
        searchValue: string
    ) => {
        try {
            setLoading(true);

            const response = await getSpecialOffers(
                page,
                searchValue
            );

            const responseData = response.data as any;

            if (response.status === 200) {
                const results = Array.isArray(responseData)
                    ? responseData
                    : responseData?.results || [];

                setOffers(results);

                setTotalPages(
                    Array.isArray(responseData)
                        ? 1
                        : responseData?.total_pages || 1
                );

                setTotalCount(
                    Array.isArray(responseData)
                        ? responseData.length
                        : responseData?.count || 0
                );
            }
        } catch (error) {
            toast.error("Failed to fetch offers");
        } finally {
            setLoading(false);
        }
    };

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setCurrentPage(1);
        }, 500);

        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        fetchOffers(currentPage, search);
    }, [currentPage, search]);
console.log(search,'search')
    // PATCH only sends active: false
    const handleToggleStatus = async (item: any) => {
        if (item.active === false) {
            toast.error("Offer is already inStatus.");
            return;
        }

        try {
            const response = await updateSpecialOffer(
                item.id,
                { active: false }
            );

            if (response.status === 200) {
                toast.success(
                    "Offer deactivated successfully"
                );

                setOffers((prev) =>
                    prev.map((x) =>
                        x.id === item.id
                            ? { ...x, active: false }
                            : x
                    )
                );
            }
        } catch (error) {
            toast.error("Failed to update status");
        }
    };

    const handleAdd = () => {
        setEditingOffer(null);
        setIsModalOpen(true);
    };

    const handleEdit = (item: any) => {
        setEditingOffer(item);
        setIsModalOpen(true);
    };

    const columns = [
        {
            key: "code",
            label: "Code",
            render: (value: string) => (
                <p className="text-gray-800">
                    {value || "—"}
                </p>
            ),
        },
        {
            key: "description",
            label: "Description",
            render: (value: string) => (
                <p className="text-gray-800">
                    {value || "—"}
                </p>
            ),
        },
        {
            key: "valid_from",
            label: "Valid From",
            render: (value: string) => (
                <p className="text-gray-800">
                    {value ? formatDate(value) : "—"}
                </p>
            ),
        },
        {
            key: "valid_to",
            label: "Valid To",
            render: (value: string) => (
                <p className="text-gray-800">
                    {value ? formatDate(value) : "—"}
                </p>
            ),
        },
        {
            key: "discount_amount",
            label: "Discount Amount",
            render: (value: string) => (
                <p className="font-semibold text-gray-800">
                    ₹ {value}
                </p>
            ),
        },
        {
            key: "active",
            label: "Status",
            render: (_: any, row: any) => (
                <button
                    onClick={() => {
                        if (canUpdate) {
                            handleToggleStatus(row);
                        }
                    }}
                    disabled={
                        !canUpdate ||
                        row.active === false
                    }
                    title={
                        row.active === false
                            ? "Already inStatus"
                            : "Click to deactivate"
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        row.active === false
                            ? "bg-red-500"
                            : "bg-green-500"
                    } ${
                        !canUpdate ||
                        row.active === false
                            ? "opacity-50 cursor-not-allowed"
                            : "cursor-pointer"
                    }`}
                >
                    <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            row.active === false
                                ? "translate-x-1"
                                : "translate-x-6"
                        }`}
                    />
                </button>
            ),
        },
    ];

    return (
        <>
            <div className="space-y-6">
                {/* Search */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />

                    <input
                        type="text"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                        }}
                        placeholder="Search special offers..."
                        className="w-full pl-9 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                    />
                </div>

                <GenericTable
                    title="Special Offers"
                    buttonName={
                        canCreate
                            ? "Add Special Offer"
                            : ""
                    }
                    handleAdd={
                        canCreate
                            ? handleAdd
                            : undefined
                    }
                    columns={columns}
                    data={offers}
                    loading={loading}
                    onEdit={
                        canUpdate
                            ? handleEdit
                            : undefined
                    }
                    emptyMessage={
                        search.trim()
                            ? "No special offers found"
                            : "No special offers found"
                    }
                />

                {!loading && totalPages > 1 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={totalCount}
                        itemsPerPage={ITEMS_PER_PAGE}
                        onPageChange={(page) =>
                            setCurrentPage(page)
                        }
                    />
                )}
            </div>

            {isModalOpen && (
                <SpecialOfferModal
                    key={editingOffer?.id || "new"}
                    offer={editingOffer}
                    onClose={() => {
                        setIsModalOpen(false);
                        setEditingOffer(null);
                    }}
                    onSuccess={() => {
                        setIsModalOpen(false);
                        setEditingOffer(null);

                        fetchOffers(
                            currentPage,
                            debouncedSearch
                        );
                    }}
                />
            )}
        </>
    );
}